// hooks/use-dashboard-users.ts
import { FormEvent, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { UserType } from '../../../pages/DashboardUsers';
import { useGetAllUsers } from '../../../apis/users/get-all-users';
import { useGetAllStates } from '../../../apis/users/get-all-states';
import { useBlockUser } from '../../../apis/users/block-user';
import { areBothDatesPresent, formatDate, isOneDatePresent } from '../../../utils/helpers';

const BLOCKED = "blocked";

const useDashboardUsers = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [user, setUser] = useState<UserType | null>(null);
    const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
    const [isBlockModalVisible, setIsBlockModalVisible] = useState(false);
    const [key, setKey] = useState(0);

    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [status, setStatus] = useState(searchParams.get("status") || "");
    const [fromDate, setFromDate] = useState<string | null>(searchParams.get("fromDate") || "");
    const [toDate, setToDate] = useState<string | null>(searchParams.get("toDate") || "");
    const [state, setState] = useState(searchParams.get("state") || "");

    const filterFormRef = useRef<HTMLFormElement>(null);
    const searchFormRef = useRef<HTMLFormElement>(null);

    const { data: usersData, isFetching: loading, refetch: refetchUsers } = useGetAllUsers(searchParams);
    const { data: statesData, isFetching: isLoadingStates } = useGetAllStates();
    const { mutateAsync: blockUser, isPending: isBlocking } = useBlockUser();

    const users = usersData?.docs ?? [];
    const totalPages = usersData?.totalPages ?? 0;
    const allStates = statesData?.states ?? [];

    const updateUrlParams = (newParams: Record<string, string>) => {
        const updatedParams = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            if (value) {
                updatedParams.set(key, value);
            } else {
                updatedParams.delete(key);
            }
        });
        setSearchParams(updatedParams);
    };

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        updateUrlParams({ search: formData.get("search") as string, page: "1" });
    };

    const handleFilter = (e: FormEvent) => {
        e.preventDefault();
        const formData = new FormData(e.target as HTMLFormElement);
        const fromDate = formData.get("fromDate") as string;
        const toDate = formData.get("toDate") as string;
        updateUrlParams({
            status: formData.get("status") as string,
            fromDate: fromDate ? formatDate(fromDate) : "",
            toDate: toDate ? formatDate(toDate) : "",
            page: "1",
            state: formData.get("state") as string,
        });
    };

    const resetFilters = () => {
        setSearch("");
        setStatus("");
        setFromDate("");
        setToDate("");
        setSearchParams({});
        setState("");
        setKey((prevKey) => prevKey + 1);
        if (searchFormRef.current) searchFormRef.current.reset();
    };

    const handleOpenBlockModal = (selectedUser: UserType) => {
        setUser(selectedUser);
        setIsBlockModalVisible(true);
    };

    const handleBlock = async () => {
        if (!user) return;
        await blockUser({ userId: user._id, isBlocked: !user.isBlocked });
        refetchUsers();
    };

    const isAnyFilterApplied = () => status !== "" || state !== "";

    const isApplyButtonDisabled = () => {
        if (isAnyFilterApplied()) return isOneDatePresent(fromDate, toDate);
        return !areBothDatesPresent(fromDate, toDate);
    };

    const isClearButtonDisabled = () =>
        status === "" && fromDate === "" && toDate === "" && search === "" && state === "";

    return {
        users,
        totalPages,
        loading,
        user,
        isCreateUserOpen,
        setIsCreateUserOpen,
        isBlockModalVisible,
        setIsBlockModalVisible,
        isBlocking,
        key,
        search,
        setSearch,
        status,
        setStatus,
        fromDate,
        setFromDate,
        toDate,
        setToDate,
        state,
        setState,
        allStates,
        isLoadingStates,
        filterFormRef,
        searchFormRef,
        searchParams,
        updateUrlParams,
        handleSearch,
        handleFilter,
        resetFilters,
        handleOpenBlockModal,
        handleBlock,
        isApplyButtonDisabled,
        isClearButtonDisabled,
        refetchUsers,
        BLOCKED,
    };
};

export default useDashboardUsers;